import React from 'react'
import { Helmet } from 'react-helmet-async'
import './PrivacyPolicyPage.css'

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Politique de confidentialité - EdenPlaisir.com</title>
        <meta name="description" content="Consultez la politique de confidentialité de EdenPlaisir.com pour comprendre comment nous protégeons vos données personnelles." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="legal-container">
        <h1>Politique de confidentialité</h1>
        <p className="last-updated">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            EdenPlaisir.com (ci-après "nous", "notre", "le Site") s'engage à protéger la confidentialité 
            de vos informations personnelles. La présente politique de confidentialité décrit la manière 
            dont nous collectons, utilisons, partageons et protégeons vos données personnelles lorsque 
            vous utilisez notre site web.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Données collectées</h2>
          <p>Nous collectons les types de données personnelles suivants :</p>
          <ul>
            <li>
              <strong>Données d'identification :</strong> nom d'utilisateur, adresse e-mail, date de naissance
            </li>
            <li>
              <strong>Données de connexion :</strong> adresse IP, logs de connexion, cookies
            </li>
            <li>
              <strong>Données de paiement :</strong> informations de transaction via notre processeur de paiement Stripe 
              (les données de carte bancaire ne sont pas stockées sur nos serveurs)
            </li>
            <li>
              <strong>Données d'utilisation :</strong> historique de navigation, préférences, interactions avec le contenu
            </li>
            <li>
              <strong>Données d'abonnement :</strong> niveau d'abonnement, dates d'activation et d'expiration, historique des factures
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Utilisation des données</h2>
          <p>Nous utilisons vos données personnelles pour :</p>
          <ul>
            <li>Fournir et améliorer nos services</li>
            <li>Gérer votre compte utilisateur et vos abonnements</li>
            <li>Traiter les paiements et gérer les factures</li>
            <li>Personnaliser votre expérience sur le site</li>
            <li>Communiquer avec vous concernant votre compte ou nos services</li>
            <li>Respecter nos obligations légales et réglementaires</li>
            <li>Prévenir la fraude et assurer la sécurité du site</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Base légale du traitement</h2>
          <p>Le traitement de vos données personnelles repose sur :</p>
          <ul>
            <li><strong>Votre consentement :</strong> lorsque vous créez un compte et acceptez cette politique</li>
            <li><strong>L'exécution d'un contrat :</strong> pour fournir les services d'abonnement que vous avez souscrits</li>
            <li><strong>Nos intérêts légitimes :</strong> pour améliorer nos services et assurer la sécurité</li>
            <li><strong>Obligations légales :</strong> pour respecter la législation applicable</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Partage des données</h2>
          <p>Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos données avec :</p>
          <ul>
            <li>
              <strong>Stripe :</strong> notre processeur de paiement, pour le traitement des transactions
            </li>
            <li>
              <strong>Prestataires de services :</strong> hébergement, analytics, support client (sous contrat de confidentialité strict)
            </li>
            <li>
              <strong>Autorités légales :</strong> uniquement si requis par la loi ou pour protéger nos droits
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Sécurité des données</h2>
          <p>
            Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées 
            pour protéger vos données personnelles contre l'accès non autorisé, la perte, la destruction 
            ou la modification. Cependant, aucune méthode de transmission sur Internet n'est totalement 
            sécurisée.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Conservation des données</h2>
          <p>
            Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos 
            services et respecter nos obligations légales. Les données sont supprimées conformément 
            à notre politique de rétention, sauf si la loi exige une conservation plus longue.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Vos droits</h2>
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <ul>
            <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
            <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
            <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
            <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
            <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
            <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
            <li><strong>Droit de retirer votre consentement :</strong> à tout moment</li>
          </ul>
          <p>
            Pour exercer ces droits, contactez-nous à l'adresse indiquée dans les mentions légales.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Cookies</h2>
          <p>
            Nous utilisons des cookies et technologies similaires pour améliorer votre expérience, 
            analyser l'utilisation du site et personnaliser le contenu. Vous pouvez gérer vos préférences 
            de cookies dans les paramètres de votre navigateur.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Transferts internationaux</h2>
          <p>
            Vos données peuvent être transférées et stockées dans des pays situés en dehors de l'Union 
            Européenne. Dans ce cas, nous nous assurons que des garanties appropriées sont en place 
            pour protéger vos données conformément au RGPD.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Modification de la politique</h2>
          <p>
            Nous pouvons modifier cette politique de confidentialité à tout moment. Les modifications 
            seront publiées sur cette page avec une date de mise à jour. Nous vous encourageons à 
            consulter régulièrement cette page.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Contact</h2>
          <p>
            Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
            veuillez nous contacter en utilisant les informations fournies dans les mentions légales.
          </p>
        </section>
      </div>
    </div>
  )
}

export default PrivacyPolicyPage

